import { isCollectionVariant } from '@ikegps/core-api';
import type { CollectionModel } from '@ikegps/core-shared';
import { action, computed, observable, type ObservableMap, makeObservable } from 'mobx';

interface PageStateRef {
	cancelInteractions(): void;
}

/**
 * An entry tracking a modified target collection.
 * sourceCollectionIds tracks which source poles have caused modifications,
 * enabling per-source cleanup in the topo editor.
 */
export interface ModifiedTargetEntry {
	collection: CollectionModel;
	sourceCollectionIds: Set<string>;
}

/**
 * State for linking a collection to a vector field.
 *
 * Modified target collections are tracked in a map keyed by collection ID.
 * Each target's proxy state `isDirty` determines whether it needs saving.
 */
export class CollectionLinkState {
	@observable fieldId: string | null = null;
	@observable collectionRevision: string | null = null;
	@observable sourceFormRevision: string | null = null;
	@observable isSpanLinkContext: boolean = false;

	modifiedTargetCollections: ObservableMap<string, ModifiedTargetEntry> = observable.map<string, ModifiedTargetEntry>(
		{},
		{ deep: false }
	);

	constructor(private pageState: PageStateRef) {
		makeObservable(this);
	}

	@computed get isLinking(): boolean {
		return this.fieldId != null;
	}

	@computed get hasModifiedTargets(): boolean {
		return this.modifiedTargetCollections.size > 0;
	}

	get hasDirtyTargets(): boolean {
		for (const [, entry] of this.modifiedTargetCollections) {
			if (entry.collection.isEditing && entry.collection.isDirty) {
				return true;
			}
		}
		return false;
	}

	@action beginLinking(
		fieldId: string,
		options?: {
			formRevision?: string;
			isSpanContext?: boolean;
		}
	) {
		this.pageState.cancelInteractions();
		this.fieldId = fieldId;
		this.sourceFormRevision = options?.formRevision ?? null;
		this.isSpanLinkContext = options?.isSpanContext ?? false;
	}

	@action cancelLinking() {
		this.fieldId = null;
		this.collectionRevision = null;
		this.sourceFormRevision = null;
		this.isSpanLinkContext = false;
	}

	/**
	 * Select `collection` as the link target. A variant is read-only, so it must never be a target — a span
	 * (or vector) link edits and saves the target collection, which would silently mutate the variant. Refuse
	 * a variant (returning false so the caller can warn) and stay in linking mode so a valid pole can be
	 * picked; returns true when the target is accepted.
	 */
	@action completeLinking(collection: CollectionModel): boolean {
		if (isCollectionVariant(collection)) {
			return false;
		}
		this.fieldId = null;
		this.collectionRevision = collection.revision;
		return true;
	}

	/**
	 * Register a target collection as modified.
	 * If an entry already exists, the sourceCollectionId is added to the set.
	 */
	@action registerModifiedTarget(collection: CollectionModel, sourceCollectionId: string) {
		const existing = this.modifiedTargetCollections.get(collection.id);
		if (existing) {
			existing.sourceCollectionIds.add(sourceCollectionId);
		} else {
			this.modifiedTargetCollections.set(collection.id, {
				collection,
				sourceCollectionIds: new Set([sourceCollectionId]),
			});
		}
	}

	/**
	 * Remove a modified target entry. Calls finishEdit if still editing.
	 */
	@action removeModifiedTarget(collectionId: string) {
		const entry = this.modifiedTargetCollections.get(collectionId);
		if (entry) {
			if (entry.collection.isEditing) {
				entry.collection.finishEdit();
			}
			this.modifiedTargetCollections.delete(collectionId);
		}
	}

	/**
	 * Remove a source's claim on modified targets.
	 * If no claims remain, remove the entry from the map.
	 * Does NOT call finishEdit — the topo editor manages each collection's
	 * edit state through its own controllers.
	 */
	@action cleanupTargetsForSource(sourceCollectionId: string) {
		const toRemove: string[] = [];
		for (const [collectionId, entry] of this.modifiedTargetCollections) {
			entry.sourceCollectionIds.delete(sourceCollectionId);
			if (entry.sourceCollectionIds.size === 0) {
				toRemove.push(collectionId);
			}
		}
		for (const id of toRemove) {
			this.modifiedTargetCollections.delete(id);
		}
	}

	/**
	 * Discard all modified targets, reverting each collection's edit state.
	 * Called when the user discards changes on the source collection.
	 */
	@action discardAllModifiedTargets() {
		for (const [, entry] of this.modifiedTargetCollections) {
			if (entry.collection.isEditing) {
				entry.collection.finishEdit();
			}
		}
		this.modifiedTargetCollections.clear();
	}

	/**
	 * Clear all modified target entries without reverting edit state.
	 * Used after successful save — the proxy state has been serialized.
	 */
	@action clearModifiedTargets() {
		this.modifiedTargetCollections.clear();
	}

	/**
	 * Returns the list of modified target collections that are dirty and need saving.
	 */
	getModifiedTargetCollections(): CollectionModel[] {
		const result: CollectionModel[] = [];
		for (const [, entry] of this.modifiedTargetCollections) {
			if (entry.collection.isEditing && entry.collection.isDirty) {
				result.push(entry.collection);
			}
		}
		return result;
	}
}
